import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Print,
  PictureAsPdf,
  Edit, 
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';
import apiService from '../../services/api';
import InvoicePrint from './InvoicePrint';
import { printInvoice, generatePDF } from '../../utils/invoiceUtils';

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(''); 

  useEffect(() => {
    fetchInvoiceData();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/invoices/${id}`);
      
      if (response.success) {
        setInvoice(response.data.invoice);
        
        // Fetch client details
        const clientResponse = await apiService.get(`/clients/${response.data.invoice.client_id}`);
        if (clientResponse.success) {
          setClient(clientResponse.data.client);
        }
        
        // Fetch tenant details
        const tenantResponse = await apiService.get('/tenant/settings');
        if (tenantResponse.success) {
          setTenant(tenantResponse.data);
        }
      } else {
        setError('Failed to fetch invoice details');
      }
    } catch (err) {
      setError('Error loading invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (invoice && client) {
      const invoiceElement = (
        <InvoicePrint 
          invoice={invoice} 
          client={client} 
          tenant={tenant}
          isPrintMode={true}
        />
      );
      printInvoice(invoiceElement);
    }
  };

  const handleDownloadPDF = async () => {
    if (invoice && client) {
      try {
        const invoiceElement = (
          <InvoicePrint 
            invoice={invoice} 
            client={client} 
            tenant={tenant}
            isPrintMode={true}
          />
        );
        const filename = `invoice-${invoice.invoice_number}.pdf`;
        await generatePDF(invoiceElement, filename);
      } catch (err) {
        setError('Error generating PDF: ' + err.message);
      }
    }
  };
 

  
 
  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      sent: 'info',
      paid: 'success',
      overdue: 'error',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard/invoices')}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Typography variant="h6" color="error" gutterBottom>
          Invoice not found
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard/invoices')}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/dashboard/invoices')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Invoice #{invoice.invoice_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(invoice.invoice_date)}
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}> 
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button> 
        </Box>
      </Box>

      {/* Status Badge */}
      <Box mb={3}>
        <Chip 
          label={getStatusLabel(invoice.status)}
          color={getStatusColor(invoice.status)}
          size="large"
        />
      </Box>

      {/* Invoice Content */}
      <Grid container spacing={3}>
        {/* Main Invoice */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <InvoicePrint 
              invoice={invoice} 
              client={client} 
              tenant={tenant}
              isPrintMode={false}
            />
          </Paper>
        </Grid>

        
      </Grid>
 
    </Box>
  );
};

export default InvoiceView; 